import { Button } from '@consta/uikit/Button'
import type { ModalNavOnChangeHandler } from '@core/components'
import { ModalBody, ModalFooter, ModalHeader, ModalNav, ModalRight } from '@core/components'
import { IconGeoSpeed } from '@core/icons'
import { usingMovingModal } from '@library/model'
import cn from 'classnames'
import { forwardRef, useCallback, useState } from 'react'

import { vsGroups } from './constants'
import { getFullNavItems } from './helpers'
import { useSimulateVelocity } from './hooks'
import { ModalSimulationRightMenu } from './ModalSimulationRightMenu'
import type {
  VelocitySimulationError,
  VelocitySimulationItem,
  VelocitySimulationItemType,
  VelocitySimulationProps,
} from './types'
import css from './VelocitySimulation.css'

const borderRadius = { borderRadius: '0.4rem' }

const VelocitySimulation = forwardRef<HTMLDivElement, VelocitySimulationProps>(
  function VelocitySimulation({ closeModal, onClose }, ref) {
    const [activeStepNew, setActiveStepNew] = useState<VelocitySimulationItemType>('model')
    const handleOnMenuChange = useCallback<ModalNavOnChangeHandler<VelocitySimulationItem>>(
      item => {
        setActiveStepNew(item.id)
      },
      [],
    )

    const {
      calculationStatus,
      formData,
      onCalculate,
      setFormData,
      modelResults,
      existingModelsList,
      chosenVelocityModel,
      onChangeVelocityModel,
      addedSelectItemList,
      setAddedSelectItemList,
      canCalculate,
      isCalculating,
      clearModalData,
      choiceValue,
      setChoiceValue,
    } = useSimulateVelocity()

    const velocitySimulationClassName = cn(css.modalBody, { [css.bodyDisabled]: isCalculating })

    const calculateButtonClassName = cn({ [css.bodyDisabled]: isCalculating })

    const isModelCreated =
      calculationStatus.status === 'Created' || calculationStatus.status === 'Success'

    const hasErrors =
      modelResults?.status === 'Failed' ||
      (modelResults?.layers.some(
        layer => layer?.status === 'ErrorMarker' || layer?.status === 'ErrorGrid',
      ) ??
        false)

    const errors: VelocitySimulationError[] = hasErrors
      ? [{ itemId: 'layers', text: calculationStatus.note ?? '' }]
      : []
    const additionalInfo: Partial<Record<VelocitySimulationItemType, string>> = {
      model: choiceValue.model.navLabel,
      layers: choiceValue.layers.navLabel,
      geometry: isModelCreated ? 'Рассчитано' : undefined,
    }

    const vsItems = getFullNavItems(additionalInfo)

    const handleCloseModal = () => {
      closeModal()
      onClose?.()
      clearModalData()
    }

    return (
      <>
        <ModalHeader
          ref={ref}
          title="Скоростное моделирование"
          icon={IconGeoSpeed}
          data-testid="velocitySimulationModal"
          onCloseClick={handleCloseModal}
        />
        <ModalBody className={velocitySimulationClassName}>
          <ModalNav
            items={vsItems}
            groups={vsGroups}
            errors={errors}
            data-testid="velocitySimulationModal__modalNavigation"
            onChoose={handleOnMenuChange}
          />
          <ModalRight
            className={css.modalRight}
            data-testid="velocitySimulationModal__modalRightMenu">
            <ModalSimulationRightMenu
              activeStep={activeStepNew}
              setChoiceValue={setChoiceValue}
              choiceValue={choiceValue}
              formData={formData}
              setFormData={setFormData}
              modelResults={modelResults}
              existingModelsList={existingModelsList}
              chosenVelocityModel={chosenVelocityModel}
              addedSelectItemList={addedSelectItemList}
              updateSelectItemList={setAddedSelectItemList}
              calculationStatus={calculationStatus}
              onChangeVelocityModel={onChangeVelocityModel}
            />
          </ModalRight>
        </ModalBody>
        <ModalFooter>
          <div />
          <Button
            style={borderRadius}
            className={calculateButtonClassName}
            label="Рассчитать"
            size="xs"
            view="primary"
            loading={isCalculating}
            data-testid="velocitySimulationModal__btnCalculate"
            disabled={!canCalculate || isCalculating}
            onClick={onCalculate}
          />
        </ModalFooter>
      </>
    )
  },
)

export const VelocitySimulationModal = usingMovingModal(VelocitySimulation)
